import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

const STORAGE_KEYS = {
  TOKEN: "token",
  REFRESH_TOKEN: "refresh_token",
  USER: "user",
  MANUAL_LOGOUT: "manual_logout",
};

const getStoredUser = () => {
  const raw = localStorage.getItem(STORAGE_KEYS.USER);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const getAccessTokenFromResponse = (data) =>
  data?.accessToken?.token ??
  data?.accessToken ??
  data?.token ??
  data?.data?.accessToken?.token ??
  data?.data?.accessToken ??
  data?.data?.token ??
  null;

const getRefreshTokenFromResponse = (data) =>
  data?.refreshToken ??
  data?.data?.refreshToken ??
  null;

const getUserFromResponse = (data) =>
  data?.user ??
  data?.qrMenuUser ??
  data?.data?.user ??
  data?.data?.qrMenuUser ??
  null;

const mergeUserPreservingBusinessFields = (prevUser, nextUser) => {
  if (!nextUser) return prevUser ?? null;
  if (!prevUser) return nextUser;

  return {
    ...prevUser,
    ...nextUser,

    businessId:
      nextUser.businessId ??
      nextUser.BusinessId ??
      nextUser.business_id ??
      prevUser.businessId ??
      prevUser.BusinessId ??
      prevUser.business_id,

    businessSubDomain:
      nextUser.businessSubDomain ??
      nextUser.BusinessSubDomain ??
      nextUser.subDomain ??
      nextUser.SubDomain ??
      nextUser.business_sub_domain ??
      prevUser.businessSubDomain ??
      prevUser.BusinessSubDomain ??
      prevUser.subDomain ??
      prevUser.SubDomain ??
      prevUser.business_sub_domain,

    subDomain:
      nextUser.subDomain ??
      nextUser.SubDomain ??
      nextUser.businessSubDomain ??
      nextUser.BusinessSubDomain ??
      prevUser.subDomain ??
      prevUser.SubDomain ??
      prevUser.businessSubDomain ??
      prevUser.BusinessSubDomain,
  };
};

const clearAuthStorage = () => {
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
};

const persistAuth = ({ accessToken, refreshToken, user }) => {
  if (accessToken) {
    localStorage.setItem(STORAGE_KEYS.TOKEN, accessToken);
  }

  if (refreshToken) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  }

  if (user) {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() =>
    localStorage.getItem(STORAGE_KEYS.TOKEN)
  );

  const [user, setUser] = useState(() => getStoredUser());
  const [authLoading, setAuthLoading] = useState(true);

  const fetchAndStoreAuthDetail = useCallback(async (baseUser = null) => {
    const detailRes = await api.getAuthDetail();
    const detailUser = detailRes?.data ?? null;

    const mergedUser = mergeUserPreservingBusinessFields(
      baseUser ?? getStoredUser(),
      detailUser
    );

    if (mergedUser) {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(mergedUser));
      setUser(mergedUser);
    }

    return mergedUser;
  }, []);

  const refreshSession = useCallback(async () => {
    const storedRefreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

    const refreshRes = await api.refreshToken(storedRefreshToken || undefined);

    const accessToken = getAccessTokenFromResponse(refreshRes?.data);
    const refreshToken = getRefreshTokenFromResponse(refreshRes?.data);
    const responseUser = getUserFromResponse(refreshRes?.data);

    if (!accessToken) {
      throw new Error("Refresh sonrası access token alınamadı.");
    }

    persistAuth({
      accessToken,
      refreshToken,
      user: responseUser,
    });

    setToken(accessToken);

    const finalUser = await fetchAndStoreAuthDetail(responseUser);

    if (finalUser) {
      setUser(finalUser);
    }

    return true;
  }, [fetchAndStoreAuthDetail]);

  const restoreSession = useCallback(async () => {
    const manualLogout = localStorage.getItem(STORAGE_KEYS.MANUAL_LOGOUT);

    if (manualLogout === "1") {
      clearAuthStorage();
      setToken(null);
      setUser(null);
      return false;
    }

    const storedToken = localStorage.getItem(STORAGE_KEYS.TOKEN);

    if (storedToken) {
      setToken(storedToken);

      try {
        await fetchAndStoreAuthDetail(getStoredUser());
        return true;
      } catch (error) {
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
        setToken(null);
      }
    }

    try {
      await refreshSession();
      return true;
    } catch (error) {
      clearAuthStorage();
      setToken(null);
      setUser(null);
      return false;
    }
  }, [fetchAndStoreAuthDetail, refreshSession]);

  const login = useCallback(
    async (email, password) => {
      setAuthLoading(true);

      try {
        const res = await api.login({ email, password });

        const accessToken = getAccessTokenFromResponse(res?.data);
        const refreshToken = getRefreshTokenFromResponse(res?.data);
        const responseUser = getUserFromResponse(res?.data);

        if (!accessToken) {
          throw new Error("Token alınamadı.");
        }

        localStorage.removeItem(STORAGE_KEYS.MANUAL_LOGOUT);

        persistAuth({
          accessToken,
          refreshToken,
          user: responseUser,
        });

        setToken(accessToken);

        let finalUser = responseUser ?? null;

        if (finalUser) {
          setUser(finalUser);
        }

        finalUser = await fetchAndStoreAuthDetail(finalUser);

        if (finalUser) {
          setUser(finalUser);
        }

        return true;
      } finally {
        setAuthLoading(false);
      }
    },
    [fetchAndStoreAuthDetail]
  );

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

    try {
      if (typeof api.revokeToken === "function" && refreshToken) {
        await api.revokeToken(refreshToken);
      }
    } catch (error) {
      console.warn("Refresh token revoke edilemedi:", error);
    } finally {
      clearAuthStorage();

      localStorage.setItem(STORAGE_KEYS.MANUAL_LOGOUT, "1");

      setToken(null);
      setUser(null);

      window.location.replace("/login");
    }
  }, []);

  const deleteAccount = useCallback(async () => {
    await api.deleteAccount("");

    clearAuthStorage();
    localStorage.setItem(STORAGE_KEYS.MANUAL_LOGOUT, "1");

    setToken(null);
    setUser(null);

    window.location.replace("/login");
  }, []);

  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      setAuthLoading(true);

      try {
        await restoreSession();
      } finally {
        if (!cancelled) {
          setAuthLoading(false);
        }
      }
    };

    initAuth();

    return () => {
      cancelled = true;
    };
  }, [restoreSession]);

  const value = {
    token,
    user,
    authLoading,
    login,
    logout,
    deleteAccount,
    isAuthenticated: Boolean(token),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return ctx;
};